/**
 * Homepage XRP Ledger ecosystem event listings.
 * UpcomingXrpLedgerEvents groups upcoming events by month.
 *
 * `image` should be the event page Open Graph / graph-tag image (1200×630 style)
 * saved under /public/images/, so every tile uses the same social preview treatment.
 * `description` is the short about-copy shown in the homepage event modal
 * (usually distilled from the Luma / event-page bio when the image is saved).
 */

export type EcosystemEventKind =
  | "Ripple"
  | "Hackathon"
  | "XRPL"
  | "Conference"
  | "B2B"
  | "Community";

/** Menu order for the homepage sort control. */
export const ECOSYSTEM_EVENT_KIND_ORDER: readonly EcosystemEventKind[] = [
  "Ripple",
  "Hackathon",
  "XRPL",
  "Conference",
  "B2B",
  "Community",
];

export type UpcomingXrpLedgerEvent = {
  id: string;
  name: string;
  kinds: readonly EcosystemEventKind[];
  /** Human-readable date range, e.g. "Oct 27–29" */
  dateLabel: string;
  /** ISO date for the <time> start (YYYY-MM-DD) */
  dateTime: string;
  /** Optional ISO end date for multi-day events */
  dateTimeEnd?: string;
  location: string;
  /** Local path to the event’s OG / graph-tag image */
  image: string;
  imageAlt: string;
  /** Short about-copy for the homepage event modal. */
  description?: string;
  href?: string;
  /** Opens href in a new tab when true (default for http(s) links). */
  external?: boolean;
  /** Modal CTA label. Defaults to “Secure your spot” on Luma, otherwise “More info”. */
  ctaLabel?: string;
};

export function isLumaEventHref(href?: string): boolean {
  return Boolean(href && /(?:^https?:\/\/)?(?:www\.)?luma\.com\//i.test(href));
}

export function getEcosystemEventCtaLabel(event: UpcomingXrpLedgerEvent): string {
  if (event.ctaLabel) return event.ctaLabel;
  if (isLumaEventHref(event.href)) return "Secure your spot";
  return "More info";
}

/** Keep entries in start-date order. The homepage Sort control can filter by kind. */
export const upcomingXrpLedgerEvents: UpcomingXrpLedgerEvent[] = [
  {
    id: "ripple-effect-uni-2026",
    name: "Ripple Effect Uni",
    kinds: ["Ripple"],
    dateLabel: "Aug 11",
    dateTime: "2026-08-11",
    location: "London",
    image: "/images/ripple_effect_uni_2026.jpg",
    imageAlt: "Ripple Effect: Opportunities on XRP Ledger at UCL London",
    description:
      "The XRP Ledger is powering a new wave of innovation, from tokenisation and payment infrastructure to autonomous AI agents. Ripple Effect brings students, developers, engineers, and blockchain enthusiasts together at UCL for an afternoon of discussion, discovery, and connection — including tokenisation, payment channels, autonomous AI agents, and builder opportunities on XRPL.",
    href: "https://luma.com/i9yy3xc6",
    external: true,
  },
  {
    id: "singhacks-2026",
    name: "SingHacks 2026",
    kinds: ["Hackathon", "Ripple"],
    dateLabel: "Sep 4–5",
    dateTime: "2026-09-04",
    dateTimeEnd: "2026-09-05",
    location: "Singapore",
    image: "/images/singhacks_2026.jpg",
    imageAlt: "SingHacks 2026: AI x Fintech hackathon in Singapore, co-organized with Ripple and Julius Baer",
    description:
      "Co-organized with Ripple and Julius Baer, SingHacks returns to Singapore on 4–5 September for Asia’s leading AI x fintech hackathon. Teams get real challenge statements from industry, then two days to build, test, and pitch working solutions to decision-makers. SGD $10,000 in prizes across wealth management, AI, and digital assets.",
    href: "https://luma.com/an9krh0p",
    external: true,
  },
  {
    id: "xrp-socal-2026",
    name: "XRP SoCal",
    kinds: ["Community"],
    dateLabel: "Sep 5",
    dateTime: "2026-09-05",
    location: "Santa Ana",
    image: "/images/xrp_socal_2026.jpg",
    imageAlt: "XRP SoCal 2026 at Mission Control Bar Arcade, Santa Ana",
    description:
      "The 5th Annual XRP SoCal brings the XRP Ledger community together in Southern California for live art, networking, and time with developers, builders, and community leaders. Share ideas, discover new opportunities, and enjoy free XRP merch, food, and drinks while supplies last. Attendees must be 21+ with valid ID.",
    href: "https://luma.com/udaodoon",
    external: true,
  },
  {
    id: "stablecoin-payments-dinner-2026",
    name: "Stablecoin & Payments Dinner",
    kinds: ["B2B"],
    dateLabel: "Sep 9",
    dateTime: "2026-09-09",
    location: "National Harbor",
    image: "/images/stablecoin_payments_dinner_2026.jpg",
    imageAlt: "Stablecoin & Payments Dinner at Stablecon 2026, hosted by Ripple, QED Investors, and t54 Labs",
    description:
      "Join Ripple, QED Investors and t54 Labs for an intimate, invite-only dinner during StableCon 2026. A small group of founders, investors and senior leaders across stablecoins, payments, financial infrastructure and AI gather for drinks, dinner, and candid conversation — no panels or presentations. Registration is subject to host approval.",
    href: "https://luma.com/91hlrbfw",
    external: true,
  },
  {
    id: "xrpl-lending-protocol-hackathon-2026",
    name: "XRPL Lending Protocol Hackathon",
    kinds: ["Hackathon", "XRPL", "Ripple"],
    dateLabel: "Sep 12–13",
    dateTime: "2026-09-12",
    dateTimeEnd: "2026-09-13",
    location: "Paris",
    image: "/images/xrpl_lending_protocol_hackathon_2026.jpg",
    imageAlt: "XRPL Lending Protocol Hackathon in Paris, hosted by DeVinci Blockchain",
    description:
      "De Vinci Blockchain hosts a curated, in-person Builder Task Force hackathon on the XRP Ledger: one track, one weekend. Build on the Lending Protocol (XLS-66) and Single Asset Vault (XLS-65), ship a prototype, and file structured feedback on the tooling and docs. About 40 to 50 builders; applications are reviewed, this is not a walk-in. Prizes include a $5,000 cash pool plus a $5,000 Swell tour travel prize for the overall winning team. Registration is subject to host approval.",
    href: "https://luma.com/t4ttb973",
    external: true,
  },
  {
    id: "xrpl-meetup-barcelona-2026",
    name: "XRPL Meetup Barcelona",
    kinds: ["XRPL"],
    dateLabel: "Sep 16",
    dateTime: "2026-09-16",
    location: "Barcelona",
    image: "/images/xrpl_meetup_barcelona_2026.png",
    imageAlt: "XRPL Meetup Barcelona at Café Il·lusió, hosted by XRPL Commons",
    description:
      "Join XRPL Commons, Peersyst and Bitvavo in the heart of Barcelona for an official European Blockchain Convention side event. Expect insights into the XRP Ledger ecosystem, project feedback from the XRPL Commons Aquarium incubator team, short talks from builders, and networking with developers, founders, and XRPL enthusiasts.",
    href: "https://luma.com/xrpl-meetup-barcelona",
    external: true,
  },
  {
    id: "agentic-payments-onchain-2026",
    name: "Agentic Payments Onchain",
    kinds: ["Ripple", "XRPL"],
    dateLabel: "Sep 28",
    dateTime: "2026-09-28",
    location: "Seoul",
    image: "/images/agentic_payments_onchain_2026.jpg",
    imageAlt: "Agentic Payments Onchain, a Korea Blockchain Week side event hosted by Ripple, t54, Tenity, and Bloom",
    description:
      "A Korea Blockchain Week official side event from Ripple, t54, Tenity, and Bloom, conducted in Korean. For builders and researchers interested in XRPL, on-chain AI infrastructure, and agentic payment standards such as x402. Talks, a panel, a fireside chat, and networking in Gangnam. Registration is subject to host approval.",
    href: "https://luma.com/mw84pfjm",
    external: true,
  },
  {
    id: "ripple-bcg-sibos-mixer-2026",
    name: "Ripple x BCG: Sibos Mixer",
    kinds: ["Ripple", "B2B"],
    dateLabel: "Sep 29",
    dateTime: "2026-09-29",
    location: "Miami Beach",
    image: "/images/ripple_bcg_sibos_mixer_2026.jpg",
    imageAlt: "Ripple x BCG Sibos Mixer in Miami Beach, presented by Ripple Events",
    description:
      "Join the Ripple and BCG teams for an evening of conversation, cocktails and canapés during Sibos Miami. Attendance is by application only; each attendee must register individually. Space is limited and registration is subject to host approval.",
    href: "https://luma.com/ripplebcgsibosmixer",
    external: true,
  },
  {
    id: "lucky-charms-squid-2026",
    name: "Lucky Charms · Squid",
    kinds: ["Community"],
    dateLabel: "Sep 29",
    dateTime: "2026-09-29",
    location: "Seoul",
    image: "/images/lucky_charms_squid_2026.png",
    imageAlt: "Lucky Charms · Squid hangout during Korea Blockchain Week in Seoul",
    description:
      "Squid is back in Seoul with a hangout for friends during Korea Blockchain Week. Join for coffee, snacks, and a little good luck for the future. Space is limited and registration is subject to host approval.",
    href: "https://luma.com/mxlw6311",
    external: true,
  },
  {
    id: "onchain-finance-exchange-seoul-2026",
    name: "Onchain Finance Exchange",
    kinds: ["Ripple", "B2B"],
    dateLabel: "Sep 30",
    dateTime: "2026-09-30",
    location: "Seoul",
    image: "/images/onchain_finance_exchange_seoul_2026.jpg",
    imageAlt:
      "Kyobo, SBI Holdings, and Ripple Onchain Finance Exchange during Korea Blockchain Week in Seoul",
    description:
      "Join Ripple, Kyobo Securities, and SBI Holdings in Seoul during Korea Blockchain Week for an evening on moving tokenized assets from pilot to production. A panel on how institutions hold and settle tokenized value — and why regulatory clarity matters — is followed by drinks and networking. Hosted in English. Registration is subject to host approval.",
    href: "https://luma.com/854sn50i",
    external: true,
  },
  {
    id: "udax-demo-day-oxford-2026",
    name: "UDAX Demo Day Oxford",
    kinds: ["Ripple"],
    dateLabel: "Oct 1",
    dateTime: "2026-10-01",
    location: "London",
    image: "/images/udax_demo_day_oxford_2026.jpg",
    imageAlt: "University Digital Asset Xcelerator Demo Day Oxford 2026 at Ripple’s London office",
    description:
      "The University Digital Asset Xcelerator’s 2026 Demo Day at Ripple’s London office. Expect pitches from 2–4pm, followed by a networking happy hour and light bites. Registration is subject to host approval.",
    href: "https://luma.com/rivbfm31",
    external: true,
  },
  {
    id: "axelar-friends-seoul-2026",
    name: "Axelar & Friends | Seoul",
    kinds: ["Community", "XRPL"],
    dateLabel: "Oct 2",
    dateTime: "2026-10-02",
    location: "Seoul",
    image: "/images/axelar_friends_seoul_2026.png",
    imageAlt: "Axelar & Friends networking meetup in Seoul during Korea Blockchain Week",
    description:
      "During Korea Blockchain Week, Axelar hosts an afternoon meetup with partners, institutions, and the broader blockchain community — including XRPL Korea. Coffee, conversation, and time with the Axelar team at Kamee the Garden in Gangnam, 1:00–4:00 PM KST. Registration is subject to host approval.",
    href: "https://luma.com/lhykeej3",
    external: true,
  },
  {
    id: "xrp-seoul-2026",
    name: "XRP Seoul",
    kinds: ["Conference"],
    dateLabel: "Oct 3",
    dateTime: "2026-10-03",
    location: "Seoul",
    image: "/images/xrp_seoul_2026.png",
    imageAlt: "XRP Seoul 2026 at Grand Hyatt Seoul",
    description:
      "XRP Seoul is a global conference at Grand Hyatt Seoul focused on the digital asset ecosystem, institutional blockchain adoption, and real-world on-chain use cases. It brings together builders, institutions, and the wider digital asset community in the heart of Asia.",
    href: "https://www.xrp-seoul.com/",
    external: true,
  },
  {
    id: "xrp-seoul-vip-afterparty-2026",
    name: "XRP Seoul VIP Afterparty",
    kinds: ["Community"],
    dateLabel: "Oct 3",
    dateTime: "2026-10-03",
    location: "Seoul",
    image: "/images/xrp_seoul_vip_afterparty_2026.jpg",
    imageAlt: "XRP Seoul 2026 VIP Afterparty at JJ Mahoney's, Grand Hyatt Seoul, hosted by XRPL Korea",
    description:
      "Continue the conversations from XRP Seoul 2026 at the official VIP afterparty. Speakers, partners, investors, financial institutions, and industry leaders gather for networking at JJ Mahoney's, Grand Hyatt Seoul. Invitation-only; registration is subject to host approval.",
    href: "https://luma.com/q0wmg82n",
    external: true,
  },
  {
    id: "ripple-daw-london-breakfast-2026",
    name: "Ripple Networking Breakfast",
    kinds: ["Ripple", "B2B"],
    dateLabel: "Oct 7",
    dateTime: "2026-10-07",
    location: "London",
    image: "/images/ripple_daw_london_breakfast_2026.jpg",
    imageAlt: "Ripple Networking Breakfast at Digital Assets Week London, presented by Ripple Events",
    description:
      "Join the Ripple team for a networking breakfast ahead of day two of Digital Assets Week London. Attendance is by application only; each attendee must register individually. Space is limited and registration is subject to host approval.",
    href: "https://luma.com/rippledigitalassetsweeklondon",
    external: true,
  },
  {
    id: "token2049-sundown-mixer-2026",
    name: "TOKEN2049 Sundown Mixer",
    kinds: ["Ripple", "B2B"],
    dateLabel: "Oct 7",
    dateTime: "2026-10-07",
    location: "Singapore",
    image: "/images/token2049_sundown_mixer_2026.png",
    imageAlt:
      "Ripple and Citadel Securities TOKEN2049 Sundown Mixer in Singapore, presented by XRP Events",
    description:
      "As TOKEN2049 Singapore winds down for the day, Ripple and Citadel Securities invite you to an exclusive sundown mixer for institutional leaders and digital asset innovators. No panels or presentations — just the right people in the room to connect, exchange ideas, and explore what’s next. Space is limited and registration is subject to host approval.",
    href: "https://luma.com/njvq6kz0",
    external: true,
  },
  {
    id: "ripple-cross-river-mixer-2026",
    name: "Ripple x Cross River Bank Mixer",
    kinds: ["Ripple", "B2B"],
    dateLabel: "Oct 19",
    dateTime: "2026-10-19",
    location: "Las Vegas",
    image: "/images/ripple_cross_river_mixer_2026.jpg",
    imageAlt: "Ripple x Cross River Bank Mixer at Money20/20 USA in Las Vegas, presented by Ripple Events",
    description:
      "Join the Ripple and Cross River Bank teams after day one of Money20/20 USA for an evening of conversation, cocktails and canapés. Attendance is by application only; each attendee must register individually. Space is limited and registration is subject to host approval.",
    href: "https://luma.com/RippleXCrossRiverBankMixer",
    external: true,
  },
  {
    id: "xrpl-core-dev-bootcamp-nyc-2026",
    name: "XRPL Core Dev Bootcamp",
    kinds: ["XRPL"],
    dateLabel: "Oct 20–22",
    dateTime: "2026-10-20",
    dateTimeEnd: "2026-10-22",
    location: "New York",
    image: "/images/xrpl_core_dev_bootcamp_nyc_2026.jpg",
    imageAlt: "XRP Ledger Core Dev Bootcamp in New York, hosted by XRPL Commons",
    description:
      "A 3-day deep dive into xrpld, the software that runs the XRP Ledger, led by Denis Angell, CTO of the XRPL Foundation. For C++ developers who want to work on the core protocol — state management, the transaction pipeline, RPC handlers, and amendments — and the on-ramp to the XRP Ledger Hackathon Protocol Innovation track the following weekend. Registration is subject to host approval.",
    href: "https://luma.com/xrpl-core-dev-bootcamp2",
    external: true,
  },
  {
    id: "xrpl-hackathon-nyc-2026",
    name: "XRP Ledger Hackathon",
    kinds: ["Hackathon"],
    dateLabel: "Oct 24–25",
    dateTime: "2026-10-24",
    dateTimeEnd: "2026-10-25",
    location: "New York",
    image: "/images/xrpl_hackathon_nyc_2026.jpg",
    imageAlt: "XRP Ledger Hackathon, Oct 24–25, New York",
    description:
      "Sprint, build, and launch on the XRP Ledger in 36 hours in New York City, the weekend before Swell. Hosted by XRPL Commons, with tracks for protocol innovation, agentic finance, lending and borrowing, and an open “why not” lane. Whether this is your first commit on XRPL or you’ve been building for years, there’s a track for you.",
    href: "https://luma.com/xrpl-hackathon-nyc",
    external: true,
  },
  {
    id: "xrp-sidekick-ny-2026",
    name: "XRP Sidekick - NY",
    kinds: ["Community"],
    dateLabel: "Oct 26",
    dateTime: "2026-10-26",
    location: "New York",
    image: "/images/xrp_sidekick_ny_2026.png",
    imageAlt: "XRP Sidekick - NY at Barcade, New York",
    description:
      "Join the XRP community for a retro night at Barcade in New York. Enjoy free merch, food, the arcade, and drinks on the hosts. Attendees must be 21+ with valid ID, and each person needs their own registration.",
    href: "https://luma.com/wxhv5mof",
    external: true,
  },
  {
    id: "fuzzycon-nyc-2026",
    name: "FuzzyCon I",
    kinds: ["Community"],
    dateLabel: "Oct 26",
    dateTime: "2026-10-26",
    location: "New York",
    image: "/images/fuzzycon_nyc_2026.jpg",
    imageAlt: "FuzzyCon I at Creature's Rooftop in New York, the night before Swell",
    description:
      "The night before Swell, FuzzyCon brings the XRP community together on a rooftop in New York. No presentations — food, drinks, Fuzzycards, merch, and a limited FuzzyCon promo card. Free entry, first come first served. Guests must be 21+ with valid ID.",
    href: "https://luma.com/dunn966x",
    external: true,
  },
  {
    id: "swell-2026-demo-day",
    name: "Swell 2026 Demo Day",
    kinds: ["Ripple"],
    dateLabel: "Oct 26",
    dateTime: "2026-10-26",
    location: "New York",
    image: "/images/swell_2026_demo_day.jpg",
    imageAlt: "Swell 2026 Demo Day at Ripple’s New York office, hosted with the University Digital Asset Xcelerator",
    description:
      "Swell 2026 Demo Day at Ripple’s New York office, hosted with the University Digital Asset Xcelerator. Pitches from 1–3pm, then a networking reception with light bites. Registration is subject to host approval.",
    href: "https://luma.com/zpcdsbpu",
    external: true,
  },
  {
    id: "ubri-connect-2026",
    name: "UBRI Connect 2026",
    kinds: ["Ripple", "Conference"],
    dateLabel: "Oct 27",
    dateTime: "2026-10-27",
    location: "New York",
    image: "/images/ubri_connect_2026.png",
    imageAlt: "UBRI Connect 2026 at Cornell Tech, New York, presented by Ripple Events",
    description:
      "UBRI Connect brings scholars, researchers and thought leaders in the UBRI network to Cornell Tech to showcase blockchain initiatives, share knowledge, and inspire future research. The day includes a networking lunch, breakout sessions, and transportation to the Swell welcome reception.",
    href: "https://luma.com/UBRI2026",
    external: true,
  },
  {
    id: "ripple-swell-2026",
    name: "Ripple Swell",
    kinds: ["Ripple", "Conference"],
    dateLabel: "Oct 27–29",
    dateTime: "2026-10-27",
    dateTimeEnd: "2026-10-29",
    location: "New York",
    image: "/images/ripple_swell_2026_og.jpg",
    imageAlt: "Ripple Swell 2026, Oct 27–29, New York",
    description:
      "Ripple Swell is Ripple’s flagship conference in New York, bringing institutions, builders, and policymakers together around the future of digital assets and on-chain finance. Three days of talks, meetings, and ecosystem gatherings around the XRP Ledger.",
    href: "https://swell.ripple.com/",
    external: true,
  },
  {
    id: "ubri-connect-student-mixer-2026",
    name: "UBRI Connect 2026 Student Mixer",
    kinds: ["Community", "Ripple"],
    dateLabel: "Oct 28",
    dateTime: "2026-10-28",
    location: "New York",
    image: "/images/ubri_connect_student_mixer_2026.jpg",
    imageAlt: "UBRI Connect 2026 Student Mixer at Cooper's Craft & Cocktails in New York",
    description:
      "A student-hosted mixer for university blockchain clubs in New York during UBRI Connect and Swell week. Stop by Cooper’s Craft & Cocktails for food and drinks and meet other student builders. Co-hosted by the University of Kansas, Cornell, NYU, Columbia, Emory, and Fordham. Valid ID is required; guests under 21 receive wristbands and cannot drink. Preference goes to university students applying with a .edu email, and registration is subject to host approval.",
    href: "https://luma.com/jbfeujth",
    external: true,
  },
  {
    id: "udax-demo-day-berkeley-2026",
    name: "UDAX Demo Day Berkeley",
    kinds: ["Ripple"],
    dateLabel: "Nov 11",
    dateTime: "2026-11-11",
    location: "San Francisco",
    image: "/images/udax_demo_day_berkeley_2026.jpg",
    imageAlt: "University Digital Asset Xcelerator Demo Day UC Berkeley 2026 at Ripple HQ in San Francisco",
    description:
      "The University Digital Asset Xcelerator’s 2026 Demo Day at Ripple HQ in San Francisco. Founder demos, team Q&A, and an investor networking reception. Registration is subject to host approval.",
    href: "https://luma.com/watbu0zq",
    external: true,
  },
  {
    id: "ixh26-italian-xrpl-hackathon-2026",
    name: "IXH26 Italian XRPL Hackathon",
    kinds: ["Hackathon", "XRPL", "Ripple"],
    dateLabel: "Nov 20–21",
    dateTime: "2026-11-20",
    dateTimeEnd: "2026-11-21",
    location: "Bari",
    image: "/images/ixh26_italian_xrpl_hackathon_2026.jpg",
    imageAlt: "IXH26 Italian XRPL Hackathon at the University of Bari Aldo Moro",
    description:
      "A two-day cryptography and blockchain hackathon at the University of Bari Aldo Moro, jointly organized by UBRI, XRPL Commons, De Componendis Cifris, and the University of Bari. Seminars, discussion, and hands-on building on advanced cryptography for blockchain and XRPL applications, with a €10,000 prize pool. Teams of 3–8; registration is subject to host approval.",
    href: "https://luma.com/IXH26-xrpl-hackathon",
    external: true,
  },
  // {
  //   id: "xrp-australia-2027",
  //   name: "XRP Australia 2027",
  //   kinds: ["Conference"],
  //   dateLabel: "Mar 18–19",
  //   dateTime: "2027-03-18",
  //   dateTimeEnd: "2027-03-19",
  //   location: "Sydney",
  //   image: "/images/xrp_australia_2027_og.png",
  //   imageAlt: "XRP Australia 2027 at Crown Sydney",
  //   href: "https://luma.com/gggbg83x",
  //   external: true,
  // },
  // {
  //   id: "xrp-london-2027",
  //   name: "XRP London 2027",
  //   kinds: ["Conference"],
  //   dateLabel: "Jun 2–4",
  //   dateTime: "2027-06-02",
  //   dateTimeEnd: "2027-06-04",
  //   location: "London",
  //   image: "/images/xrp_london_2027_og.png",
  //   imageAlt: "XRP London 2027 at Magazine London",
  //   href: "https://luma.com/i4tjvzx1",
  //   external: true,
  // },
];

/** Local calendar date as YYYY-MM-DD. */
export function localDateISO(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Inclusive end date used for past/upcoming split. */
export function getEventEndDateISO(event: UpcomingXrpLedgerEvent): string {
  return event.dateTimeEnd ?? event.dateTime;
}

/** Past once the calendar day after the event end date begins. */
export function isXrpLedgerEventPast(
  event: UpcomingXrpLedgerEvent,
  todayISO: string = localDateISO(),
): boolean {
  return getEventEndDateISO(event) < todayISO;
}

export function getActiveUpcomingXrpLedgerEvents(
  events: UpcomingXrpLedgerEvent[] = upcomingXrpLedgerEvents,
  todayISO: string = localDateISO(),
): UpcomingXrpLedgerEvent[] {
  return events
    .filter((event) => !isXrpLedgerEventPast(event, todayISO))
    .sort((a, b) => a.dateTime.localeCompare(b.dateTime));
}

export function getPastXrpLedgerEvents(
  events: UpcomingXrpLedgerEvent[] = upcomingXrpLedgerEvents,
  todayISO: string = localDateISO(),
): UpcomingXrpLedgerEvent[] {
  return events
    .filter((event) => isXrpLedgerEventPast(event, todayISO))
    .sort((a, b) => getEventEndDateISO(b).localeCompare(getEventEndDateISO(a)));
}

export type UpcomingXrpLedgerMonthGroup = {
  key: string;
  label: string;
  events: UpcomingXrpLedgerEvent[];
};

function formatMonthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-GB", {
    month: "long",
    timeZone: "UTC",
  });
}

/** Groups upcoming events by start month, soonest month first. */
export function groupXrpLedgerEventsByMonth(
  events: UpcomingXrpLedgerEvent[],
): UpcomingXrpLedgerMonthGroup[] {
  const groups = new Map<string, UpcomingXrpLedgerEvent[]>();
  for (const event of events) {
    const key = event.dateTime.slice(0, 7);
    const list = groups.get(key);
    if (list) list.push(event);
    else groups.set(key, [event]);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, monthEvents]) => ({
      key,
      label: formatMonthLabel(key),
      events: [...monthEvents].sort((a, b) => a.dateTime.localeCompare(b.dateTime)),
    }));
}
